import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CadastralParcelFeatureDto } from '@workspace/data-types';
import { LeafletService } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import type { GeoJsonObject } from 'geojson';
import type * as LeafletTypes from 'leaflet';
import { catchError, finalize, of } from 'rxjs';

@Component({
  selector: 'etxe-property-cadastral-map',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './cadastral-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block w-full',
  },
})
export class PropertyCadastralMapComponent implements AfterViewInit, OnDestroy {
  private readonly leafletService = inject(LeafletService);
  private readonly apiService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  // Inputs
  lat = input.required<number>();
  lng = input.required<number>();
  currentReference = input<string | null | undefined>();
  selectedReferences = input<string[]>([]);
  initialParcels = input<CadastralParcelFeatureDto[]>([]);
  highlightedParcel = input<CadastralParcelFeatureDto | null | undefined>();
  interactive = input<boolean>(true);

  // Output
  parcelSelected = output<CadastralParcelFeatureDto>();

  // Synchronisation de la référence sélectionnée : s'initialise avec l'input et s'actualise au clic
  readonly selectedReference = linkedSignal(() => this.currentReference());

  // State
  readonly radius = signal<number>(25);
  readonly parcels = signal<CadastralParcelFeatureDto[]>([]);
  readonly hoveredParcel = signal<CadastralParcelFeatureDto | null>(null);
  readonly activeParcel = signal<CadastralParcelFeatureDto | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  constructor() {
    effect(() => {
      const curRef = this.currentReference()?.trim();
      const refs = this.selectedReferences();
      const hp = this.highlightedParcel();
      const allParcels = this.parcels();

      if (hp && hp.geometry) {
        this.parcels.update((existing) => {
          if (existing.some((p) => p.id === hp.id)) return existing;
          return [hp, ...existing];
        });
        this.selectedReference.set(hp.id);
        this.activeParcel.set(hp);

        // Centrer la carte sur la parcelle surlignée si disponible
        if (this.map) {
          const geom = hp.geometry as { type: string; coordinates: number[][][][] | number[][][] };
          const coords = geom?.coordinates;
          if (coords) {
            const ring =
              geom.type === 'MultiPolygon' ? (coords as number[][][][])[0]?.[0] : (coords as number[][][])[0];
            if (Array.isArray(ring) && ring.length > 0) {
              const centerLng = ring.reduce((s: number, p: number[]) => s + p[0], 0) / ring.length;
              const centerLat = ring.reduce((s: number, p: number[]) => s + p[1], 0) / ring.length;
              this.map.panTo([centerLat, centerLng], { animate: true });
            }
          }
        }
      } else if (refs.length > 0) {
        const match = allParcels.find((p) => this.isSelectedParcel(p));
        this.activeParcel.set(match ?? null);
      } else if (curRef) {
        this.selectedReference.set(curRef);
        const match = allParcels.find((p) => this.isSelectedParcel(p));
        this.activeParcel.set(match ?? null);
      } else {
        this.selectedReference.set(null);
        this.activeParcel.set(null);
      }

      if (this.map && this.parcels().length > 0) {
        this.renderParcelsOnMap(this.parcels());
      }
    });
  }

  mapContainer = viewChild<ElementRef<HTMLElement>>('mapContainer');

  private map: LeafletTypes.Map | undefined;
  private geoJsonLayerGroup: LeafletTypes.LayerGroup | undefined;
  private L: typeof LeafletTypes | undefined;

  isSelectedParcel(parcel: CadastralParcelFeatureDto): boolean {
    const parcelId = parcel.id.trim().toUpperCase();
    const shortRef = `${parcel.section} ${parcel.numero}`.trim().toUpperCase();
    const shortRefNoSpace = `${parcel.section}${parcel.numero}`.trim().toUpperCase();

    const refs = this.selectedReferences();
    if (refs && refs.length > 0) {
      return refs.some((r) => {
        const cur = r.trim().toUpperCase();
        return (
          parcelId === cur ||
          cur.includes(parcelId) ||
          parcelId.endsWith(cur) ||
          cur === shortRef ||
          cur === shortRefNoSpace
        );
      });
    }

    const cur = this.selectedReference()?.trim().toUpperCase();
    if (!cur) return false;
    return (
      parcelId === cur ||
      cur.includes(parcelId) ||
      parcelId.endsWith(cur) ||
      cur === shortRef ||
      cur === shortRefNoSpace
    );
  }

  ngAfterViewInit(): void {
    void this.initMapAndLoadParcels();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }

  setRadius(newRadius: number): void {
    if (this.radius() === newRadius) return;
    this.radius.set(newRadius);
    this.loadParcels();
  }

  private async initMapAndLoadParcels(): Promise<void> {
    const container = this.mapContainer()?.nativeElement;
    if (!container) return;

    try {
      this.L = await this.leafletService.getLeaflet();
      if (!this.L) return;

      const lat = this.lat();
      const lng = this.lng();

      // Désactiver zoomControl en haut à gauche pour éviter la superposition avec la légende
      this.map = this.L.map(container, {
        zoomControl: false,
        scrollWheelZoom: this.interactive(),
        doubleClickZoom: this.interactive(),
        dragging: this.interactive(),
        attributionControl: false,
      }).setView([lat, lng], 19);

      // Placer le contrôle de zoom en bas à droite
      this.L.control.zoom({ position: 'bottomright' }).addTo(this.map);

      // Fond satellite Esri avec maxNativeZoom à 18 pour éviter "Map data not yet available"
      this.L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          maxNativeZoom: 18,
          maxZoom: 21,
        },
      ).addTo(this.map);

      // Marqueur discret de l'adresse BAN
      const icon = this.L.divIcon({
        className: 'custom-address-marker',
        html: `<div style="width: 14px; height: 14px; background: var(--color-blue-9); border: 2.5px solid var(--color-gray-contrast); border-radius: 9999px; box-shadow: 0 0 10px var(--color-blue-9);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      this.L.marker([lat, lng], { icon }).addTo(this.map);

      this.geoJsonLayerGroup = this.L.layerGroup().addTo(this.map);

      this.loadParcels();
    } catch (_err) {
      this.error.set('Impossible de charger la carte satellite.');
    }
  }

  private loadParcels(): void {
    const preloaded = this.initialParcels();
    if (preloaded.length > 0) {
      this.parcels.set(preloaded);
      this.renderParcelsOnMap(preloaded);
      const foundCurrent = preloaded.find((p) => this.isSelectedParcel(p));
      this.activeParcel.set(foundCurrent ?? null);
      return;
    }

    const lat = this.lat();
    const lng = this.lng();
    const radius = this.radius();

    this.isLoading.set(true);
    this.error.set(null);

    this.apiService
      .business()
      .etxe()
      .public()
      .gouv()
      .cadastreSurrounding(lat, lng, radius)
      .pipe(
        catchError(() => of([])),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((parcels) => {
        this.parcels.set(parcels);
        this.renderParcelsOnMap(parcels);

        // Si une parcelle correspond à la sélection existante, on l'active
        const foundCurrent = parcels.find((p) => this.isSelectedParcel(p));
        if (foundCurrent) {
          this.activeParcel.set(foundCurrent);
        } else {
          this.activeParcel.set(null);
        }
      });
  }

  private renderParcelsOnMap(parcels: CadastralParcelFeatureDto[]): void {
    if (!this.map || !this.L || !this.geoJsonLayerGroup) return;

    this.geoJsonLayerGroup.clearLayers();

    parcels.forEach((parcel) => {
      if (!parcel.geometry) return;

      const isSelected = this.isSelectedParcel(parcel);
      const hasBuilding = Boolean(parcel.hasBdnbBuilding);

      // Style conditionnel :
      // - Sélectionnée : Vert jade éclatant
      // - Mitoyenne bâtie BDNB : Ambre vif
      // - Non bâtie / sans BDNB : Grisé discret atténué (non sélectionnable)
      const baseStyle: LeafletTypes.PathOptions = isSelected
        ? {
            color: 'var(--color-jade-9)',
            weight: 3,
            fillColor: 'var(--color-jade-9)',
            fillOpacity: 0.35,
          }
        : hasBuilding
          ? {
              color: 'var(--color-amber-9)',
              weight: 1.5,
              fillColor: 'var(--color-gray-contrast)',
              fillOpacity: 0.08,
              dashArray: '4, 4',
            }
          : {
              color: 'var(--color-gray-7)',
              weight: 1,
              fillColor: 'var(--color-gray-9)',
              fillOpacity: 0.03,
              dashArray: '2, 4',
            };

      if (!this.L) return;
      const layer = this.L.geoJSON(parcel.geometry as GeoJsonObject, {
        style: baseStyle,
      });

      // Tooltip avec numéro et statut bâti
      const surfaceTxt = parcel.surface ? ` · ${parcel.surface} m²` : '';
      const statusTxt = hasBuilding ? '' : ' (non bâti)';
      const label = `${parcel.section} ${parcel.numero}${surfaceTxt}${statusTxt}`;
      layer.bindTooltip(label, {
        permanent: true,
        direction: 'center',
        className: `cadastral-tooltip ${isSelected ? 'current' : ''} ${!hasBuilding ? 'no-building' : ''}`,
      });

      if (this.interactive()) {
        layer.on('mouseover', () => {
          this.hoveredParcel.set(parcel);
          (layer as LeafletTypes.GeoJSON).setStyle({
            weight: isSelected ? 3.5 : 2.5,
            color: isSelected
              ? 'var(--color-jade-9)'
              : hasBuilding
                ? 'var(--color-sky-9)'
                : 'var(--color-gray-8)',
            fillColor: isSelected
              ? 'var(--color-jade-9)'
              : hasBuilding
                ? 'var(--color-blue-9)'
                : 'var(--color-gray-8)',
            fillOpacity: isSelected ? 0.45 : hasBuilding ? 0.25 : 0.1,
            dashArray: undefined,
          });
        });

        layer.on('mouseout', () => {
          this.hoveredParcel.set(null);
          layer.setStyle(baseStyle);
        });

        layer.on('click', () => {
          this.selectParcel(parcel);
        });
      }

      this.geoJsonLayerGroup?.addLayer(layer);
    });
  }

  selectParcel(parcel: CadastralParcelFeatureDto): void {
    this.selectedReference.set(parcel.id);
    this.activeParcel.set(parcel);
    this.parcelSelected.emit(parcel);
    this.renderParcelsOnMap(this.parcels());
  }
}
